/**
 * Voice builder → guided-wizard mapper (Phase B).
 *
 * Maps the Phase-A parser-brain output (VoiceBuilderLoad — spoken stops with
 * raw date_text, equipment, weight, ambiguities) onto the guided wizard's
 * WizardFields — the SAME data model Type mode produces, so the existing
 * Review step and saveDirectLoadPost path work unchanged.
 *
 * Deterministic and unit-tested: dates resolve via resolveDatePhrase against
 * the caller's local today, equipment maps through the shared enum helper,
 * and geocoding is INJECTED (the component passes the city-DB resolver; tests
 * pass a fake). No Supabase imports here.
 *
 * Phase-A nit guards live here too:
 *  - a spurious temperature on non-reefer equipment is dropped, never shown
 *  - unresolvable/spurious date phrases never land on a stop silently — the
 *    stop falls back to the step-level date and stopDates flags it for the
 *    needs-confirmation UI
 */

import { isSpokenDatePhrase, resolveDatePhrase, type ResolvedDatePhrase } from "./resolveDatePhrase";
import { voiceEquipmentToEnum, isReeferEquipment } from "./voiceEquipment";
import type { VoiceBuilderLoad } from "./voiceBuilderTypes";
import type { WizardFields, WizardStop } from "@/components/lr-direct/guided-wizard/wizardTypes";

export interface VoiceGeoResult {
  city: string;
  state: string;
  lat: number | null;
  lon: number | null;
}

/** Injected geocoder — the component passes the shared city-DB resolver. */
export type VoiceGeocodeFn = (city: string, state: string) => Promise<VoiceGeoResult | null>;

export interface VoiceStopDateInfo extends ResolvedDatePhrase {
  /** "spoken" = a date phrase was heard; "default" = fell back to the step date. */
  source: "spoken" | "default";
}

export interface VoiceWizardMapResult {
  patch: Partial<WizardFields>;
  /** Per-stop date resolution (keyed by voice stop id) for the card UI. */
  stopDates: Record<string, VoiceStopDateInfo>;
}

/**
 * Spoken handling phrase → the form's handling chip label where one exists.
 * Mirrors HANDLING_OPTIONS in DirectPublishCard (kept as a local table so this
 * module stays UI-free and unit-testable); unmatched phrases pass through
 * verbatim — handlingRequirements is a free string[].
 */
const HANDLING_SYNONYMS: Array<[RegExp, string]> = [
  [/^drop\s*(?:&|and)?\s*hook$/i, "Drop & Hook"],
  [/^no[\s-]?touch(\s?freight)?$/i, "No Touch"],
  [/^haz[\s-]?mat(erials?)?$/i, "Hazmat"],
  [/tarps?|tarping/i, "Tarping Required"],
  [/driver\s?assist|lumper|helper/i, "Driver Assist"],
  [/temp(erature)?[\s-]?controlled|continuous/i, "Temperature Controlled"],
];

export function mapVoiceHandling(heard: string[]): string[] {
  const out: string[] = [];
  for (const raw of heard) {
    const h = String(raw || "").trim();
    if (!h) continue;
    const mapped = HANDLING_SYNONYMS.find(([re]) => re.test(h))?.[1] ?? h;
    if (!out.some((x) => x.toLowerCase() === mapped.toLowerCase())) out.push(mapped);
  }
  return out;
}

export async function mapVoiceLoadToWizard(
  load: VoiceBuilderLoad,
  opts: {
    today: Date;
    current: WizardFields;
    geocode?: VoiceGeocodeFn;
  },
): Promise<VoiceWizardMapResult> {
  const { today, current, geocode } = opts;

  // Pickups first, then drops (relative spoken order preserved within each
  // group) — the order the wizard model and save path expect.
  const pickups = load.stops.filter((s) => s.stop_type === "pickup");
  const drops = load.stops.filter((s) => s.stop_type === "drop");
  const ordered = [...pickups, ...drops];

  // Geocode every stop (parallel; failures fall back to the spoken text).
  const geos = await Promise.all(
    ordered.map(async (s): Promise<VoiceGeoResult | null> => {
      if (!s.city.trim() || !geocode) return null;
      try {
        return await geocode(s.city.trim(), s.state.trim());
      } catch {
        return null;
      }
    }),
  );

  // Resolve every spoken date phrase deterministically.
  const stopDates: Record<string, VoiceStopDateInfo> = {};
  const resolvedDates = ordered.map((s) => {
    const hasPhrase = isSpokenDatePhrase(s.date_text);
    const r = resolveDatePhrase(s.date_text, today);
    stopDates[s.id] = { ...r, source: hasPhrase ? "spoken" : "default" };
    return r.date;
  });

  // Step-level dates: first resolved pickup / last resolved drop win, falling
  // back to whatever the wizard already has (today default on pickup).
  const firstResolved = (idxs: number[]) => idxs.map((i) => resolvedDates[i]).find(Boolean) ?? null;
  const pickupIdxs = ordered.map((s, i) => (s.stop_type === "pickup" ? i : -1)).filter((i) => i >= 0);
  const dropIdxs = ordered.map((s, i) => (s.stop_type === "drop" ? i : -1)).filter((i) => i >= 0);
  const pickupDate = firstResolved(pickupIdxs) ?? current.pickupDate;
  const deliveryDate =
    [...dropIdxs].reverse().map((i) => resolvedDates[i]).find(Boolean) ?? current.deliveryDate;

  const prevById = new Map(current.stops.map((s) => [s.id, s]));

  const stops: WizardStop[] = ordered.map((s, i) => {
    const geo = geos[i];
    const city = (geo?.city ?? s.city).trim();
    const state = (geo?.state ?? s.state ?? "").trim().toUpperCase();
    const prev = prevById.get(s.id);
    const unchanged = !!prev && prev.city === city && prev.state === state;
    const fallbackDate =
      s.stop_type === "pickup" ? pickupDate : deliveryDate || pickupDate;
    return {
      id: s.id,
      stop_type: s.stop_type === "pickup" ? "pickup" : "delivery",
      location: city ? (state ? `${city}, ${state}` : city) : "",
      name: unchanged ? prev!.name : "",
      address: unchanged ? prev!.address : "",
      city,
      state,
      postal_code: unchanged ? prev!.postal_code : "",
      country: "US",
      latitude: geo?.lat ?? null,
      longitude: geo?.lon ?? null,
      scheduled_date: resolvedDates[i] ?? fallbackDate,
    };
  });

  const equipmentEnum = load.equipment ? voiceEquipmentToEnum(load.equipment) : null;
  // Nit guard: temperature only survives on reefer equipment — a hallucinated
  // "0°F" on a dry van is dropped here, never displayed or saved.
  const reefer = isReeferEquipment(load.equipment) || equipmentEnum === "reefer";
  const temp = reefer && load.temperature_f !== null ? load.temperature_f : null;

  const handling = mapVoiceHandling(load.handling);
  if (temp !== null && !handling.includes("Temperature Controlled")) {
    handling.push("Temperature Controlled");
  }

  const noteBase = load.notes?.trim() ?? "";
  const tempNote = temp !== null ? `Temp: ${temp}°F` : "";
  const comments =
    noteBase && tempNote ? `${noteBase} · ${tempNote}` : noteBase || tempNote || current.comments;

  return {
    patch: {
      pickupCount: Math.max(1, pickups.length),
      dropCount: Math.max(1, drops.length),
      stops,
      pickupDate,
      deliveryDate,
      equipmentType: equipmentEnum ?? current.equipmentType,
      weight: load.weight_lbs && load.weight_lbs > 0 ? String(load.weight_lbs) : current.weight,
      length: load.length_ft && load.length_ft > 0 ? String(load.length_ft) : current.length,
      commodity: load.commodity ?? current.commodity,
      handlingRequirements: handling,
      comments,
    },
    stopDates,
  };
}

